import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { Plus, Search, Trash2, Settings, BookOpen } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ko';

dayjs.extend(relativeTime);
dayjs.locale('ko');

const PRESET_COLORS = ['#818cf8','#f87171','#4ade80','#fb923c','#22d3ee','#facc15','#e879f9','#f472b6','#a78bfa','#34d399'];

// ── 카테고리 관리 모달 ───────────────────────────────────────────
function CategoryModal({ categories, onClose, onRefresh }) {
  const [form, setForm] = useState({ name: '', color: '#818cf8' });
  const [editing, setEditing] = useState(null);

  const save = async () => {
    if (!form.name.trim()) return;
    if (editing) await api.put(`/research-logs/categories/${editing.id}`, form);
    else await api.post('/research-logs/categories', form);
    setEditing(null);
    setForm({ name: '', color: '#818cf8' });
    onRefresh();
  };

  const remove = async (id) => {
    if (!confirm('삭제하시겠습니까?')) return;
    await api.delete(`/research-logs/categories/${id}`);
    onRefresh();
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold text-sm">카테고리 관리</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-lg leading-none">×</button>
        </div>
        <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
          {categories.map(c => (
            <div key={c.id} className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2">
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
              <span className="text-white text-sm flex-1">{c.name}</span>
              <button onClick={() => { setEditing(c); setForm({ name: c.name, color: c.color }); }}
                className="text-gray-500 hover:text-white p-1 text-xs">수정</button>
              <button onClick={() => remove(c.id)} className="text-gray-500 hover:text-red-400 p-1"><Trash2 size={12} /></button>
            </div>
          ))}
          {categories.length === 0 && <p className="text-gray-600 text-xs text-center py-2">등록된 카테고리가 없습니다.</p>}
        </div>
        <div className="border-t border-gray-800 pt-3">
          <p className="text-gray-400 text-xs mb-2">{editing ? '카테고리 수정' : '카테고리 추가'}</p>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="카테고리 이름" onKeyDown={e => e.key === 'Enter' && save()}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-brand mb-2" />
          <div className="flex items-center gap-1.5 mb-3 flex-wrap">
            {PRESET_COLORS.map(c => (
              <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                className={`w-5 h-5 rounded-full border-2 transition-transform ${form.color === c ? 'border-white scale-125' : 'border-transparent hover:scale-110'}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
          <div className="flex gap-2">
            {editing && (
              <button onClick={() => { setEditing(null); setForm({ name: '', color: '#818cf8' }); }}
                className="px-3 py-1.5 text-gray-400 text-sm hover:text-white">취소</button>
            )}
            <button onClick={save} className="px-4 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80">
              {editing ? '수정' : '추가'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 메인 목록 ────────────────────────────────────────────────────
export default function ResearchLogPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = JSON.parse(localStorage.getItem('mfg_user') || '{}');
  const isAdmin = user.role === 'superadmin' || user.role === 'admin';

  const [categories, setCategories] = useState([]);
  const selectedCatId = searchParams.get('cat') || 'all';
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [showCatModal, setShowCatModal] = useState(false);
  const [showCatPicker, setShowCatPicker] = useState(false);

  const LIMIT = 30;

  const loadCategories = useCallback(() =>
    api.get('/research-logs/categories').then(r => setCategories(r.data)), []);

  const loadLogs = useCallback(() => {
    const params = { page, limit: LIMIT };
    if (selectedCatId !== 'all') params.categoryId = selectedCatId;
    if (search.trim()) params.search = search.trim();
    api.get('/research-logs', { params }).then(r => {
      setLogs(r.data.logs || []);
      setTotal(r.data.total || 0);
    });
  }, [selectedCatId, page, search]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  const doSearch = () => { setPage(1); setSearch(searchInput); };
  const selectCat = (id) => {
    setSearchParams(id === 'all' ? {} : { cat: id });
    setPage(1); setSearch(''); setSearchInput('');
  };

  const handleCreate = () => {
    if (selectedCatId === 'all' && categories.length > 0) {
      setShowCatPicker(true);
      return;
    }
    doCreate(selectedCatId !== 'all' ? selectedCatId : null);
  };

  const doCreate = async (categoryId) => {
    const r = await api.post('/research-logs', {
      title: '새 연구일지',
      content: '',
      categoryId: categoryId || null,
    });
    navigate(`/work/product/research/${r.data.id}`);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!confirm('삭제하시겠습니까?')) return;
    await api.delete(`/research-logs/${id}`);
    loadLogs();
  };

  const totalPages = Math.ceil(total / LIMIT);

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html || '';
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <PageHeader icon={BookOpen} title="연구일지" count={total}>
        <button onClick={handleCreate}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80">
          <Plus size={14} /> 작성
        </button>
      </PageHeader>
      {/* 카테고리 탭 */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-end gap-0 overflow-x-auto scrollbar-none">
          <button onClick={() => selectCat('all')}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex-shrink-0 ${
              selectedCatId === 'all' ? 'text-brand border-brand' : 'text-gray-400 border-transparent hover:text-white hover:border-gray-600'
            }`}>
            전체
          </button>
          {categories.map(c => (
            <button key={c.id} onClick={() => selectCat(String(c.id))}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex-shrink-0 flex items-center gap-1.5`}
              style={{
                color: selectedCatId === String(c.id) ? c.color : undefined,
                borderBottomColor: selectedCatId === String(c.id) ? c.color : 'transparent',
              }}
            >
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
              {c.name}
            </button>
          ))}
        </div>
        {isAdmin && (
          <button onClick={() => setShowCatModal(true)}
            className="pb-1 p-1.5 text-gray-600 hover:text-white rounded hover:bg-gray-800 flex-shrink-0" title="카테고리 관리">
            <Settings size={14} />
          </button>
        )}
      </div>

      <div className="border-b border-gray-800 mb-4 flex-shrink-0" />

      {/* 검색 + 추가 */}
      <div className="flex gap-2 mb-4 flex-shrink-0">
        <div className="flex-1 flex items-center bg-gray-900 border border-gray-800 rounded-lg px-3 min-w-0">
          <Search size={14} className="text-gray-500 flex-shrink-0" />
          <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doSearch()}
            placeholder="제목 또는 내용 검색..."
            className="flex-1 bg-transparent text-white text-sm py-2 px-2 focus:outline-none min-w-0" />
          <button onClick={doSearch} className="flex-shrink-0 px-3 py-1 bg-gray-700 text-gray-300 text-sm rounded-md hover:bg-gray-600">검색</button>
        </div>
      </div>

      {/* 목록 */}
      <div className="flex-1 overflow-y-auto">
        {logs.length === 0 ? (
          <div className="text-center py-16 text-gray-600 text-sm">
            {search ? '검색 결과가 없습니다.' : '연구일지가 없습니다.'}
          </div>
        ) : (
          <div className="space-y-1.5">
            {logs.map(log => {
              const canDelete = log.createdBy === user.id || isAdmin;
              const excerpt = stripHtml(log.content);
              return (
                <div key={log.id}
                  className="flex items-start gap-3 bg-gray-900 border border-gray-800 rounded-lg px-4 py-3 hover:border-gray-700 cursor-pointer transition-colors group"
                  onClick={() => navigate(`/work/product/research/${log.id}`)}
                >
                  {log.category && (
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: log.category.color }} />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{log.title}</p>
                    {excerpt && (
                      <p className="text-gray-500 text-xs truncate mt-0.5">{excerpt}</p>
                    )}
                    <div className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                      {log.category && (
                        <span className="flex items-center gap-1" style={{ color: log.category.color + 'cc' }}>
                          {log.category.name}
                        </span>
                      )}
                      <span>{log.author?.name}</span>
                      <span>{dayjs(log.createdAt).format('MM.DD HH:mm')}</span>
                    </div>
                  </div>
                  {canDelete && (
                    <button onClick={e => handleDelete(e, log.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-500 hover:text-red-400 flex-shrink-0">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6 pb-4">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => Math.abs(p - page) <= 2 || p === 1 || p === totalPages)
              .map((p, i, arr) => (
                <span key={p}>
                  {i > 0 && arr[i - 1] !== p - 1 && <span className="text-gray-600 px-1">…</span>}
                  <button onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded-lg text-sm ${p === page ? 'bg-brand text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}>
                    {p}
                  </button>
                </span>
              ))}
          </div>
        )}
      </div>

      {showCatModal && (
        <CategoryModal
          categories={categories}
          onClose={() => setShowCatModal(false)}
          onRefresh={loadCategories}
        />
      )}

      {showCatPicker && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowCatPicker(false)}>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 w-full max-w-xs" onClick={e => e.stopPropagation()}>
            <h2 className="text-white font-bold text-sm mb-3">카테고리 선택 <span className="text-brand">*</span></h2>
            <div className="space-y-1.5">
              {categories.map(c => (
                <button key={c.id}
                  onClick={() => { setShowCatPicker(false); doCreate(c.id); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors text-left">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
                  <span className="text-white text-sm">{c.name}</span>
                </button>
              ))}
            </div>
            <button onClick={() => setShowCatPicker(false)}
              className="mt-3 w-full text-center text-gray-500 text-xs hover:text-gray-300">취소</button>
          </div>
        </div>
      )}
    </div>
  );
}
